import React, { useCallback, useEffect, useState } from 'react';
import ConfirmModal from './ConfirmModal';
import './MySpotModal.css';

// Your own car, on the web. The phone's SpotDetails, carrying only what a browser can honestly do.
//
// 2026-10-17: this replaces the owner's side drawer, which listed Spot ID, Time to Expire, Spot
// Type and Comments — four fields about a LISTING. This is about a car. A modal rather than a
// drawer because that is what the phone shows and because it is a thing you open, read and close.
//
// WHAT THE PHONE HAS THAT IS NOT HERE, and why, since all three were asked for:
//
//   Photo    parkPhoto.js says it in capitals — IT NEVER LEAVES THE DEVICE. A photo of a street
//            may carry faces and number plates, which is personal data belonging to people who
//            installed nothing. Showing it here means uploading it, which reverses that decision
//            and buys a moderation duty for imagery of public space.
//   Note     same rule, same reasoning: on the phone, never uploaded, gone when the car is.
//   Reminder a local iOS notification. The server knows nothing about it, so a web copy could not
//            set the phone's reminder, could not tell you one already existed, and would die with
//            the tab. Worse than absent. A server-side reminder pushed to the phone would be
//            better than either, and is a feature rather than a port.
//
// And three the owner asked to leave out, all correctly: distance (from a desk, it is the desk's
// distance), Tell others (publishing is a promise — see the previous commit), and Navigate.

// Prepends the date only when the park is not from today, so the common case stays short. Same rule
// and same reasoning as the phone's formatParkedAt: a 17-hour overnight park is tested behaviour,
// and a bare time is ambiguous once one has happened.
const formatParkedAt = (when) => {
  const d = new Date(when);
  if (isNaN(d.getTime())) return null;
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const now = new Date();
  const isToday = d.getFullYear() === now.getFullYear()
    && d.getMonth() === now.getMonth()
    && d.getDate() === now.getDate();
  return isToday ? time : `${d.toLocaleDateString([], { month: 'short', day: 'numeric' })}, ${time}`;
};

const sinceText = (when) => {
  const ms = Date.now() - new Date(when).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const mins = Math.round(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h < 24) return m ? `${h}h ${m}m ago` : `${h}h ago`;
  const days = Math.round(h / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
};

const MySpotModal = ({ spot, onClose, onDelete, addNotification }) => {
  const [address, setAddress] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const lat = spot ? Number(spot.lat ?? spot.latitude) : NaN;
  const lon = spot ? Number(spot.lng ?? spot.longitude) : NaN;
  const hasPoint = Number.isFinite(lat) && Number.isFinite(lon);

  useEffect(() => {
    if (!hasPoint) return undefined;
    let cancelled = false;
    setAddress(null);
    (async () => {
      try {
        const res = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
        const data = await res.json();
        if (!cancelled) setAddress(data.display_name || null);
      } catch (e) {
        // The street name is a nicety; the coordinates below are the thing that matters.
        if (!cancelled) setAddress(null);
      }
    })();
    return () => { cancelled = true; };
  }, [lat, lon, hasPoint]);

  // Tell ONE person where the car is. Not the map, and not other Udal drivers — the phone's own
  // comment draws that line and this is the same side of it.
  //
  // A google.com/maps link rather than a Udal one: whoever receives this almost certainly does not
  // have the app, and a link they cannot open is worse than no link. It carries the EXACT position,
  // which is the point — the fuzzing protects a spot from other users of this app, not from
  // somebody the owner has decided to tell.
  //
  // The phone attaches the note here, because "level 3, blue zone" is the half a map pin cannot
  // carry. The web has no note (see the header), so this is the address and the link.
  const handleShare = useCallback(async () => {
    if (!hasPoint) return;
    const link = `https://www.google.com/maps/search/?api=1&query=${lat.toFixed(6)},${lon.toFixed(6)}`;
    const message = ['My car is parked here.', address || null, link].filter(Boolean).join('\n');
    // navigator.share exists on Safari and on phones; everywhere else the clipboard is the honest
    // equivalent, and saying which one happened matters — a button that silently did nothing
    // visible is how people conclude it is broken.
    try {
      if (navigator.share) {
        await navigator.share({ text: message });
        return;
      }
    } catch (e) {
      return; // the sheet was dismissed; nothing was promised and nothing is owed
    }
    try {
      await navigator.clipboard.writeText(message);
      addNotification?.('Location copied — paste it to whoever needs it.', 'green');
    } catch (e) {
      addNotification?.('Could not copy the location.', 'red');
    }
  }, [hasPoint, lat, lon, address, addNotification]);

  if (!spot) return null;

  const parkedAt = spot.parked_at || spot.declared_at;
  const parkedLabel = parkedAt ? formatParkedAt(parkedAt) : null;
  const since = parkedAt ? sinceText(parkedAt) : null;

  return (
    <>
      <div className="myspot-overlay" onClick={onClose} role="presentation">
        <div
          className="myspot-content"
          role="dialog"
          aria-modal="true"
          aria-labelledby="myspot-title"
          onClick={(e) => e.stopPropagation()}
        >
          <button type="button" className="myspot-close" onClick={onClose} aria-label="Close">&times;</button>
          {/* "Your parked car", not "Your car": Profile -> Car details is already a dialog titled
              "Your car" and it is about the VEHICLE — plate, colour, type. This one is about where
              that vehicle is. Two dialogs with one name is a thing people learn to distrust. */}
          <h2 className="myspot-title" id="myspot-title">Your parked car</h2>

          <dl className="myspot-facts">
            <dt>Parked at</dt>
            <dd>
              {parkedLabel || '—'}
              {since && <span className="myspot-since">{since}</span>}
            </dd>

            <dt>Where</dt>
            <dd>
              {address === null
                ? <span className="myspot-muted">Looking up the street…</span>
                : address}
              {hasPoint && (
                <span className="myspot-coords">{lat.toFixed(5)}, {lon.toFixed(5)}</span>
              )}
            </dd>
          </dl>

          <div className="btn-row myspot-actions">
            <button type="button" className="btn btn--primary" onClick={handleShare} disabled={!hasPoint}>
              Send to someone
            </button>
            <button type="button" className="btn btn--danger-quiet" onClick={() => setConfirmDelete(true)}>
              Delete spot
            </button>
          </div>

          {/* Said once, because the question it answers — "why can't I do the rest of this here?" —
              is otherwise answered by the absence of buttons, which reads as something missing. */}
          <p className="myspot-note">
            The photo, the note and the parking reminder live on your phone.
          </p>
        </div>
      </div>

      <ConfirmModal
        isOpen={confirmDelete}
        title="Delete this spot?"
        message="Udal will forget where your car is until it detects the next park. If anybody has asked for the space, their request goes too."
        confirmLabel="Delete"
        tone="danger"
        onConfirm={() => { setConfirmDelete(false); onDelete(spot.id); onClose(); }}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
};

export default MySpotModal;
